// Visualize adds one thing: the zone version's outlines drawn over a visible Genius Zones map.

import type { ManifestLayer } from "../types/contract";
import { MIN_KRIGE_POINTS } from "./checkAccuracy";
import {
  CORE_PROPS,
  correctedCores,
  everyZoneHasCores,
  type CoreValues,
  type GeniusZone,
  type LabRow,
} from "./geniusCores";
import { zoneVersionIdOf } from "./targetLayers";

export const GENIUS_METHOD = "genius_zones" as const;
export const GENIUS_SAVE_PROPS: string[] = CORE_PROPS.map((p) => p.key);

/* * One core as `model:run` carries it: the displayed (corrected) values, stored M3 / 8″ basis. */
export interface GeniusSaveCore {
  lng: number;
  lat: number;
  /* * the zone's `zone_number` */
  zone: number;
  values: CoreValues;
}

export function geniusSaveCores(
  placements: { lng: number; lat: number; values: CoreValues }[][],
  zones: GeniusZone[],
  labs: LabRow[],
  eventId: string | null,
): GeniusSaveCore[] {
  return correctedCores(placements, zones, labs, eventId).map(
    ({ lng, lat, zone, values }) => ({ lng, lat, zone, values }),
  );
}

/* * Why Save can't run yet, or null when it can. */
export function geniusSaveBlock(
  placements: unknown[][],
  zones: GeniusZone[],
  eventId: string | null,
  modelReady: boolean,
): string | null {
  if (!eventId || !zoneVersionIdOf(eventId))
    return "Saving needs the zones lab event this walk was opened from.";
  if (!modelReady) return "Waiting for the AI map to load.";
  if (!everyZoneHasCores(placements, zones))
    return "Place at least one core in every zone.";
  const n = placements.reduce((s, g) => s + g.length, 0);
  if (n < MIN_KRIGE_POINTS)
    return `Place at least ${MIN_KRIGE_POINTS} cores in total to save the map.`;
  return null;
}

export function isGeniusMap(
  layer: Pick<ManifestLayer, "id" | "method" | "title">,
): boolean {
  if (!layer.id?.startsWith("ml-")) return false;
  return (
    layer.method === GENIUS_METHOD ||
    /\(genius zones\)/i.test(layer.title ?? "")
  );
}

/* * The zone version to outline in Visualize: the first visible Genius Zones soil map's lab event. */
export function geniusOutlineVersion(
  layers: ManifestLayer[] | null | undefined,
  isVisible: (layer: ManifestLayer) => boolean,
): string | null {
  for (const l of layers ?? []) {
    if (!isGeniusMap(l) || !isVisible(l)) continue;
    const vid = l.sample_event_id ? zoneVersionIdOf(l.sample_event_id) : null;
    if (vid) return vid;
  }
  return null;
}
